import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Paper,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, ExpandLess, ExpandMore, Fullscreen, FullscreenExit, MoreVert, Sensors } from '@mui/icons-material';
import api from '../../services/api';
import type { MachineConnection, MachineLive, MachineStopView, TelemetryLive } from '../../types/factory';
import { EmptyState, PageHeader, TONES } from '../../components/ui/PageKit';
import { errorMessage, fmtQty } from './shared';

// Inyectoras en vivo: cada máquina tiene un aparato (sensor de ciclos + módulo con red) que avisa cada golpe a DashFlow.
// Acá se ve si está produciendo o parada, desde cuándo, cómo va el turno y se carga el motivo de cada parada.

const REFRESH_MS = 5000;
const TZ = 'America/Argentina/Buenos_Aires';

const STATE: Record<MachineLive['state'], { label: string; color: string; bg: string }> = {
  produciendo: { label: 'Produciendo', color: TONES.ok.dot, bg: TONES.ok.bg },
  parada: { label: 'Parada', color: TONES.bad.dot, bg: TONES.bad.bg },
  'sin-comunicacion': { label: 'Sin comunicación', color: TONES.neutral.dot, bg: TONES.neutral.bg },
  esperando: { label: 'Esperando el primer golpe', color: TONES.info.dot, bg: TONES.info.bg },
  'no-conectada': { label: 'No conectada', color: '#C9CDD3', bg: '#F6F6F4' },
};

const fmtDur = (s: number) => {
  if (s < 60) return `${Math.max(0, Math.round(s))} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h${m % 60 ? ` ${m % 60} min` : ''}`;
};
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: TZ }) : '—');
const fmtSec = (n?: number | null) => (n == null ? '—' : `${n.toLocaleString('es-AR', { maximumFractionDigits: 1 })} s`);

const FactoryLivePage: React.FC = () => {
  const [data, setData] = useState<TelemetryLive | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skew, setSkew] = useState(0); // reloj del servidor − reloj de la compu
  const [tick, setTick] = useState(0);
  const [connection, setConnection] = useState<MachineConnection | null>(null);
  const [full, setFull] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api.getTelemetryLive()
      .then(d => { setData(d); setSkew(new Date(d.serverTime).getTime() - Date.now()); setError(null); })
      .catch(e => setError(errorMessage(e, 'No se pudo traer el estado de las máquinas')));
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    const s = window.setInterval(() => setTick(x => x + 1), 1000);
    return () => { window.clearInterval(t); window.clearInterval(s); };
  }, [load]);

  useEffect(() => {
    const onChange = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const now = Date.now() + skew;
  void tick;

  const machines = useMemo(() => {
    const order: MachineLive['state'][] = ['parada', 'sin-comunicacion', 'produciendo', 'esperando', 'no-conectada'];
    return [...(data?.machines ?? [])].sort((a, b) => order.indexOf(a.state) - order.indexOf(b.state) || a.name.localeCompare(b.name, 'es'));
  }, [data]);

  const connect = async (m: MachineLive, rotate = false) => {
    try { setConnection(await api.connectMachine(m.machineId, rotate)); load(); } catch (e) { setError(errorMessage(e, 'No se pudo conectar la máquina')); }
  };
  const showConnection = async (m: MachineLive) => {
    try { setConnection(await api.getMachineConnection(m.machineId)); } catch (e) { setError(errorMessage(e, 'No se pudo traer la conexión')); }
  };
  const disconnect = async (m: MachineLive) => {
    try { await api.disconnectMachine(m.machineId); load(); } catch (e) { setError(errorMessage(e, 'No se pudo desconectar')); }
  };
  const setReason = async (stop: MachineStopView, reason: string | null) => {
    try { await api.setStopReason(stop.id, { reason }); load(); } catch (e) { setError(errorMessage(e, 'No se pudo guardar el motivo')); }
  };

  const connected = machines.filter(m => m.connected);
  const producing = connected.filter(m => m.state === 'produciendo').length;
  const stopped = connected.filter(m => m.state === 'parada').length;

  return (
    <Box ref={rootRef} sx={{ bgcolor: full ? '#F4F3F0' : undefined, p: full ? 3 : 0, minHeight: full ? '100vh' : undefined, overflow: full ? 'auto' : undefined }}>
      <PageHeader
        title="Inyectoras en vivo"
        description={data
          ? `Turno ${data.shift.label} (${fmtTime(data.shift.from)} a ${fmtTime(data.shift.to)}) · ${producing} produciendo · ${stopped} parada${stopped === 1 ? '' : 's'} · se actualiza cada 5 segundos`
          : 'Estado de cada máquina según los golpes que avisa su aparato.'}
        actions={(
          <Tooltip title={full ? 'Salir de pantalla completa' : 'Pantalla completa (para la tele de la planta)'}>
            <IconButton onClick={() => (full ? document.exitFullscreen() : rootRef.current?.requestFullscreen())}>{full ? <FullscreenExit /> : <Fullscreen />}</IconButton>
          </Tooltip>
        )}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!data ? <Box sx={{ height: 200 }} /> : machines.length === 0 ? (
        <EmptyState title="No hay máquinas cargadas" hint="Cargá las inyectoras en Configuración y después conectalas acá." />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' } }}>
          {machines.map(m => (
            <MachineCard key={m.machineId} m={m} now={now} reasons={data.reasons}
              onConnect={() => connect(m)} onShowConnection={() => showConnection(m)} onRotate={() => connect(m, true)}
              onDisconnect={() => disconnect(m)} onReason={setReason} />
          ))}
        </Box>
      )}

      <ConnectionDialog connection={connection} onClose={() => setConnection(null)} />
    </Box>
  );
};

const MachineCard: React.FC<{
  m: MachineLive; now: number; reasons: string[];
  onConnect: () => void; onShowConnection: () => void; onRotate: () => void; onDisconnect: () => void;
  onReason: (s: MachineStopView, reason: string | null) => void;
}> = ({ m, now, reasons, onConnect, onShowConnection, onRotate, onDisconnect, onReason }) => {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [showStops, setShowStops] = useState(false);
  const st = STATE[m.state];
  const sinceSec = m.since ? (now - new Date(m.since).getTime()) / 1000 : null;
  const shiftTotal = m.shift.producingSeconds + m.shift.stoppedSeconds;
  const prodPct = shiftTotal ? m.shift.producingSeconds / shiftTotal : 0;
  const closedStops = m.stops.filter(s => s.endAt);

  if (!m.connected) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderStyle: 'dashed', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 750, fontSize: 18 }}>{m.name}</Typography>
          <Typography variant="body2" color="text.secondary">{m.moldName ? `Matriz ${m.moldName} · ` : ''}No está conectada todavía</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Sensors />} onClick={onConnect} sx={{ textTransform: 'none' }}>Conectar</Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', borderLeft: `6px solid ${st.color}` }}>
      <Box sx={{ p: 2.25, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 19, letterSpacing: '-0.02em' }}>{m.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {m.moldName ? `Matriz ${m.moldName}` : 'Sin matriz montada'}{m.cavitiesUseful ? ` · ${m.cavitiesUseful} bocas` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={e => setMenu(e.currentTarget)} aria-label="Opciones de conexión"><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            <MenuItem onClick={() => { setMenu(null); onShowConnection(); }}>Ver URL del aparato</MenuItem>
            <MenuItem onClick={() => { setMenu(null); onRotate(); }}>Cambiar la clave</MenuItem>
            <MenuItem onClick={() => { setMenu(null); onDisconnect(); }} sx={{ color: 'error.main' }}>Desconectar</MenuItem>
          </Menu>
        </Stack>

        <Box sx={{ mt: 1.5, px: 1.5, py: 1.1, borderRadius: 2, bgcolor: st.bg }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, alignSelf: 'center', boxShadow: m.state === 'produciendo' ? `0 0 0 4px ${st.color}33` : undefined }} />
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{st.label}</Typography>
            {sinceSec != null && m.state !== 'esperando' && (
              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {m.state === 'sin-comunicacion' ? `desde las ${fmtTime(m.since)}` : `hace ${fmtDur(sinceSec)}`}
              </Typography>
            )}
          </Stack>
          {m.state === 'sin-comunicacion' && (
            <Typography variant="caption" color="text.secondary">El aparato no avisa hace más de 3 minutos: puede estar sin luz o sin internet.</Typography>
          )}
        </Box>

        {m.openStop && (
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {m.openStop.reason ? 'Motivo de esta parada:' : '¿Por qué está parada?'}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
              {reasons.map(r => (
                <Chip key={r} label={r} size="small" clickable
                  color={m.openStop?.reason === r ? 'error' : 'default'} variant={m.openStop?.reason === r ? 'filled' : 'outlined'}
                  onClick={() => onReason(m.openStop!, m.openStop?.reason === r ? null : r)} />
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Turno {m.shift.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {fmtDur(m.shift.producingSeconds)} produciendo · {fmtDur(m.shift.stoppedSeconds)} parada
            </Typography>
          </Stack>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: shiftTotal ? TONES.bad.bg : '#ECEAE6', overflow: 'hidden' }}>
            <Box sx={{ width: `${prodPct * 100}%`, height: '100%', bgcolor: TONES.ok.dot, transition: 'width .6s ease' }} />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 1 }}>
          <Stat label="Golpes" value={fmtQty(m.shift.cycles)} />
          <Stat label="Piezas" value={m.shift.units == null ? '—' : fmtQty(m.shift.units)} />
          <Stat label="Ciclo real" value={fmtSec(m.realCycleSeconds)}
            hint={`esperado ${fmtSec(m.expectedCycleSeconds)}`}
            tone={m.realCycleSeconds && m.realCycleSeconds > m.expectedCycleSeconds * 1.1 ? 'warn' : undefined} />
          <Stat label="Paradas" value={String(m.shift.stops)}
            hint={m.shift.stopsWithoutReason ? `${m.shift.stopsWithoutReason} sin motivo` : 'todas con motivo'}
            tone={m.shift.stopsWithoutReason ? 'bad' : undefined} />
        </Box>

        <LastHour values={m.lastHour} />
      </Box>

      <Box sx={{ borderTop: '1px solid #ECEAE6' }}>
        <Button fullWidth size="small" onClick={() => setShowStops(v => !v)} endIcon={showStops ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: 'none', color: 'text.secondary', py: 0.75 }}>
          Paradas del día ({closedStops.length}) · {fmtDur(m.day.stoppedSeconds)} en total
        </Button>
        <Collapse in={showStops} unmountOnExit>
          <Box sx={{ px: 2, pb: 1.5 }}>
            {closedStops.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Sin paradas terminadas hoy.</Typography>
            ) : closedStops.map(s => (
              <Stack key={s.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.6, borderBottom: '1px dashed #ECEAE6' }}>
                <Typography variant="body2" sx={{ width: 104, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(s.startAt)} a {fmtTime(s.endAt)}</Typography>
                <Typography variant="body2" sx={{ width: 72, fontWeight: 650 }}>{fmtDur(s.seconds)}</Typography>
                <TextField select size="small" value={s.reason ?? ''} onChange={e => onReason(s, e.target.value || null)}
                  sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 13 } }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value=""><em>Sin motivo</em></MenuItem>
                  {reasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
                {s.noData && <Tooltip title="Al detectarla el aparato tampoco avisaba: puede ser falta de datos"><Chip size="small" label="sin datos" /></Tooltip>}
              </Stack>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

const Stat: React.FC<{ label: string; value: string; hint?: string; tone?: 'bad' | 'warn' }> = ({ label, value, hint, tone }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: 17, fontVariantNumeric: 'tabular-nums', color: tone ? TONES[tone].fg : undefined }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: 11, color: tone ? TONES[tone].fg : 'text.secondary' }}>{hint}</Typography>}
  </Box>
);

/** Golpes por minuto de la última hora: los huecos son las paradas. */
const LastHour: React.FC<{ values: number[] }> = ({ values }) => {
  const max = Math.max(1, ...values);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">Última hora, golpes por minuto</Typography>
      <Stack direction="row" alignItems="flex-end" spacing="2px" sx={{ height: 34, mt: 0.5 }}>
        {values.map((v, i) => (
          <Box key={i} title={`${v} golpes`} sx={{ flex: 1, height: v ? `${Math.max(12, (v / max) * 100)}%` : '3px', borderRadius: '2px', bgcolor: v ? '#F0561D' : TONES.bad.bg }} />
        ))}
      </Stack>
    </Box>
  );
};

const ConnectionDialog: React.FC<{ connection: MachineConnection | null; onClose: () => void }> = ({ connection, onClose }) => {
  const copy = (t: string) => { void navigator.clipboard?.writeText(t); };
  const Row: React.FC<{ label: string; value: string; hint: string }> = ({ label, value, hint }) => (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, p: 1, bgcolor: '#F6F6F4', borderRadius: 1.5 }}>
        <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, flex: 1, wordBreak: 'break-all' }}>{value}</Typography>
        <IconButton size="small" onClick={() => copy(value)} aria-label={`Copiar ${label}`}><ContentCopy fontSize="small" /></IconButton>
      </Stack>
    </Box>
  );
  return (
    <Dialog open={!!connection} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Conexión de {connection?.machineName}</DialogTitle>
      <DialogContent dividers>
        {connection && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Configurá el aparato de la máquina para que llame a estas direcciones. La clave es lo único que identifica a la
              máquina: no la compartas. Si se filtra, usá "Cambiar la clave".
            </Typography>
            <Row label="Cada golpe" value={connection.cycleUrl} hint="Una llamada por cada ciclo de la máquina (por ejemplo, cuando abre el molde)." />
            <Row label="Sigo vivo, cada 1 minuto" value={connection.heartbeatUrl} hint="Si no llega por 3 minutos, la máquina figura sin comunicación en vez de parada." />
            <Row label="Envío por lotes (POST)" value={connection.url}
              hint='Para aparatos que guardan los golpes sin internet: {"events":[{"at":"2026-11-03T20:15:02Z","kind":"cycle"},{"kind":"heartbeat"}]}' />
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Listo</Button></DialogActions>
    </Dialog>
  );
};

export default FactoryLivePage;
